import React, { useState, useMemo, useEffect } from 'react';
import { Plus, X, Users, BarChart2, ClipboardList, CheckCircle, FileText, Trash2, Edit2, ChevronDown, ChevronRight } from 'lucide-react';
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid, ReferenceLine } from 'recharts';
import { toast } from '../utils/toast';

// ─── Constantes ───────────────────────────────────────────────────────────────

const QUESTOES_RH = [
  { id: 'q1',  texto: 'A empresa reconhece formalmente os riscos psicossociais como parte da gestão de SST.', categoria: 'Estrutura' },
  { id: 'q2',  texto: 'Existe um mapeamento atualizado de riscos psicossociais por área ou função.', categoria: 'Estrutura' },
  { id: 'q3',  texto: 'Os riscos psicossociais estão integrados ao PGR (Programa de Gerenciamento de Riscos).', categoria: 'Estrutura' },
  { id: 'q4',  texto: 'Há um responsável ou equipe definida para tratar temas de saúde mental e clima organizacional.', categoria: 'Estrutura' },
  { id: 'q5',  texto: 'A empresa realiza pesquisas de clima organizacional ou engajamento com frequência definida.', categoria: 'Monitoramento' },
  { id: 'q6',  texto: 'Existem canais seguros e confidenciais para denúncias ou relatos de assédio e sobrecarga.', categoria: 'Monitoramento' },
  { id: 'q7',  texto: 'A liderança recebe capacitação sobre riscos psicossociais, assédio e saúde mental.', categoria: 'Monitoramento' },
  { id: 'q8',  texto: 'Há acompanhamento de indicadores como absenteísmo, turnover e afastamentos por saúde mental.', categoria: 'Monitoramento' },
  { id: 'q9',  texto: 'A empresa possui políticas formais contra assédio moral e sexual amplamente comunicadas.', categoria: 'Políticas' },
  { id: 'q10', texto: 'Os colaboradores recebem orientação sobre equilíbrio entre vida pessoal e trabalho.', categoria: 'Políticas' },
  { id: 'q11', texto: 'Existe acompanhamento do nível de carga de trabalho e distribuição de demandas entre equipes.', categoria: 'Políticas' },
  { id: 'q12', texto: 'A empresa realiza ações de prevenção ao burnout e estresse ocupacional.', categoria: 'Políticas' },
  { id: 'q13', texto: 'Há integração entre RH, SST e lideranças na análise de riscos psicossociais.', categoria: 'Integração' },
  { id: 'q14', texto: 'Os dados de clima, saúde e segurança são utilizados para tomada de decisão estratégica.', categoria: 'Integração' },
  { id: 'q15', texto: 'A organização documenta ações e planos de melhoria relacionados à saúde mental.', categoria: 'Integração' },
  { id: 'q16', texto: 'Existe um plano de ação contínuo para mitigação de riscos psicossociais.', categoria: 'Integração' },
  { id: 'q17', texto: 'A empresa oferece programas de apoio psicológico ou parcerias externas.', categoria: 'Programas' },
  { id: 'q18', texto: 'As ações de bem-estar são mensuradas com indicadores claros.', categoria: 'Programas' },
  { id: 'q19', texto: 'A comunicação interna aborda temas de saúde mental de forma recorrente.', categoria: 'Programas' },
  { id: 'q20', texto: 'A empresa utiliza sistemas ou plataformas para registrar, acompanhar e evidenciar conformidade com a NR-1.', categoria: 'Programas' },
];

const QUESTOES_FUNCIONARIOS = [
  { id: 'f1', texto: 'Como você avalia seu nível de estresse no trabalho atualmente?', tipo: 'opcoes', opcoes: ['Baixo', 'Médio', 'Alto'] },
  { id: 'f2', texto: 'Você sente que o trabalho tem afetado negativamente sua saúde mental?', tipo: 'sim_nao', opcoes: ['Sim', 'Não'] },
  { id: 'f3', texto: 'Você sente que tem um bom equilíbrio entre vida pessoal e profissional?', tipo: 'sim_nao', opcoes: ['Sim', 'Não'] },
  { id: 'f4', texto: 'A empresa oferece suporte adequado para sua saúde mental?', tipo: 'sim_nao', opcoes: ['Sim', 'Não'] },
  { id: 'f5', texto: 'Você tem hábitos saudáveis, como prática de exercícios físicos e alimentação adequada?', tipo: 'opcoes', opcoes: ['Sim', 'Não', 'Às vezes'] },
  { id: 'f6', texto: 'Existe histórico de transtornos mentais na sua família?', tipo: 'sim_nao', opcoes: ['Sim', 'Não'] },
  { id: 'f7', texto: 'Você faz uso de algum medicamento para questões de saúde mental?', tipo: 'sim_nao', opcoes: ['Sim', 'Não'] },
  { id: 'f8', texto: 'Você notou alguma mudança na sua saúde mental desde que começou a trabalhar aqui?', tipo: 'sim_nao', opcoes: ['Sim', 'Não'] },
  { id: 'f9', texto: 'Você se sente acolhido e apoiado pelo RH da empresa?', tipo: 'sim_nao', opcoes: ['Sim', 'Não'] },
];

const NIVEIS = [
  { nivel: 1, faixa: '0–39', label: 'Baixa Preparação', cor: '#ef4444', descricao: 'Iniciativas tendem a ser pontuais, pouco documentadas ou reativas. A empresa ainda não estruturou a gestão de riscos psicossociais conforme a NR-1.' },
  { nivel: 2, faixa: '40–69', label: 'Média Preparação', cor: '#f59e0b', descricao: 'Existem ações, mas falta de integração e consistência dificulta comprovar conformidade. Há potencial significativo de melhoria.' },
  { nivel: 3, faixa: '70–100', label: 'Alta Preparação', cor: '#22c55e', descricao: 'Ações são contínuas, documentadas e conectadas à estratégia. A empresa demonstra maturidade na gestão de riscos psicossociais.' },
];

const CATEGORIAS = ['Estrutura', 'Monitoramento', 'Políticas', 'Integração', 'Programas'];
const CORES_PIZZA = ['#3b82f6', '#ef4444', '#f59e0b', '#22c55e', '#a855f7'];

const novoId = () => `nr1_${Date.now()}_${Math.random().toString(36).slice(2)}`;

function calcularTotal(respostas) {
  return QUESTOES_RH.reduce((s, q) => s + (Number(respostas?.[q.id]) || 0), 0);
}

function calcularNivel(total) {
  if (total >= 70) return 3;
  if (total >= 40) return 2;
  return 1;
}

// ─── Engine de Análise Inteligente NR-1 ──────────────────────────────────────

const RECOMENDACOES_POR_QUESTAO = {
  q1:  { acao: 'Formalizar política de gestão de riscos psicossociais com aprovação da diretoria', prazo: '30 dias', responsavel: 'RH / Diretoria', prioridade: 'CRÍTICA' },
  q2:  { acao: 'Realizar mapeamento de riscos psicossociais por área/função com metodologia documentada', prazo: '60 dias', responsavel: 'SST / RH', prioridade: 'CRÍTICA' },
  q3:  { acao: 'Integrar riscos psicossociais ao PGR existente com cronograma de revisão anual', prazo: '45 dias', responsavel: 'SST', prioridade: 'CRÍTICA' },
  q4:  { acao: 'Designar responsável formal (pessoa ou comitê) para gestão de saúde mental e clima', prazo: '15 dias', responsavel: 'Diretoria / RH', prioridade: 'CRÍTICA' },
  q5:  { acao: 'Implementar pesquisa de clima com periodicidade mínima anual e análise de resultados', prazo: '90 dias', responsavel: 'RH', prioridade: 'ALTA' },
  q6:  { acao: 'Criar canal de denúncias anônimo (ouvidoria ou ferramenta digital) com procedimento documentado', prazo: '30 dias', responsavel: 'RH / Compliance', prioridade: 'ALTA' },
  q7:  { acao: 'Incluir módulo de riscos psicossociais e saúde mental no programa de desenvolvimento de lideranças', prazo: '60 dias', responsavel: 'RH / T&D', prioridade: 'ALTA' },
  q8:  { acao: 'Estruturar dashboard de indicadores: absenteísmo, turnover e afastamentos por saúde mental com acompanhamento mensal', prazo: '45 dias', responsavel: 'RH / DP', prioridade: 'ALTA' },
  q9:  { acao: 'Elaborar e comunicar política antissédio com treinamento obrigatório para todos os colaboradores', prazo: '30 dias', responsavel: 'RH / Jurídico', prioridade: 'CRÍTICA' },
  q10: { acao: 'Desenvolver programa de qualidade de vida com orientações sobre jornada, férias e desconexão digital', prazo: '60 dias', responsavel: 'RH', prioridade: 'MÉDIA' },
  q11: { acao: 'Implantar processo de monitoramento de carga de trabalho com reuniões periódicas entre gestores e equipes', prazo: '45 dias', responsavel: 'Gestores / RH', prioridade: 'ALTA' },
  q12: { acao: 'Criar programa de prevenção ao burnout com ações educativas, pausas e acompanhamento de casos', prazo: '60 dias', responsavel: 'RH / Medicina do Trabalho', prioridade: 'ALTA' },
  q13: { acao: 'Estabelecer comitê interdisciplinar (RH + SST + Lideranças) com reuniões mensais para análise de riscos psicossociais', prazo: '30 dias', responsavel: 'Diretoria', prioridade: 'ALTA' },
  q14: { acao: 'Integrar indicadores de clima e saúde ao relatório estratégico da empresa com apresentação à diretoria', prazo: '60 dias', responsavel: 'RH / Diretoria', prioridade: 'MÉDIA' },
  q15: { acao: 'Implementar registro formal de ações de melhoria de saúde mental com evidências documentadas', prazo: '30 dias', responsavel: 'RH / SST', prioridade: 'ALTA' },
  q16: { acao: 'Elaborar plano de ação contínuo de mitigação de riscos psicossociais com metas e responsáveis', prazo: '45 dias', responsavel: 'SST / RH', prioridade: 'CRÍTICA' },
  q17: { acao: 'Contratar ou firmar parceria para suporte psicológico (psicólogo, EAP ou plataforma de saúde mental)', prazo: '60 dias', responsavel: 'RH', prioridade: 'ALTA' },
  q18: { acao: 'Definir indicadores de bem-estar (NPS interno, taxa de utilização de benefícios) e acompanhar mensalmente', prazo: '45 dias', responsavel: 'RH', prioridade: 'MÉDIA' },
  q19: { acao: 'Incluir pautas de saúde mental na comunicação interna (newsletter, murais, reuniões) com frequência mínima mensal', prazo: '30 dias', responsavel: 'Comunicação / RH', prioridade: 'MÉDIA' },
  q20: { acao: 'Adotar sistema para registrar, acompanhar e evidenciar conformidade com NR-1 (plataforma ou planilha estruturada)', prazo: '45 dias', responsavel: 'SST / TI', prioridade: 'ALTA' },
};

const ANALISE_CATEGORIA = {
  Estrutura:     { descricao: 'Governança e formalização da gestão de riscos psicossociais', impacto: 'Base para toda conformidade NR-1 — sem estrutura, demais ações não se sustentam' },
  Monitoramento: { descricao: 'Capacidade de identificar e acompanhar riscos no dia a dia', impacto: 'Detecta problemas antes que virem afastamentos ou litígios' },
  Políticas:     { descricao: 'Normas e práticas formais de proteção ao trabalhador', impacto: 'Proteção legal e cultural contra assédio e sobrecarga' },
  Integração:    { descricao: 'Conexão entre áreas e uso estratégico de dados de saúde', impacto: 'Transforma dados em decisões; sem integração, ações ficam isoladas' },
  Programas:     { descricao: 'Ações concretas de suporte e bem-estar ao colaborador', impacto: 'Percepção do colaborador sobre comprometimento da empresa com saúde mental' },
};

function gerarRelatorioTexto(diagRH, pesquisa, questoesRH = QUESTOES_RH) {
  if (!diagRH) return '';
  const total = questoesRH.reduce((s, q) => s + (Number(diagRH.respostas?.[q.id]) || 0), 0);
  const maxPossivel = questoesRH.length * 5;
  const totalNormalizado = maxPossivel > 0 ? Math.round(total / maxPossivel * 100) : 0;
  const nivel = calcularNivel(totalNormalizado);
  const info = NIVEIS.find(n => n.nivel === nivel);

  const categorias = [...new Set(questoesRH.map(q => q.categoria))];

  // ── Análise por categoria ──
  const analiseCategoria = categorias.map(cat => {
    const qs = questoesRH.filter(q => q.categoria === cat);
    const maxCat = qs.length * 5;
    const obtido = qs.reduce((s, q) => s + (Number(diagRH.respostas?.[q.id]) || 0), 0);
    const pct = maxCat > 0 ? Math.round(obtido / maxCat * 100) : 0;
    const criticos = qs.filter(q => (Number(diagRH.respostas?.[q.id]) || 0) === 0);
    const emDesenv = qs.filter(q => (Number(diagRH.respostas?.[q.id]) || 0) === 3);
    const fortes = qs.filter(q => (Number(diagRH.respostas?.[q.id]) || 0) === 5);
    const status = pct >= 80 ? '✅ Consolidado' : pct >= 50 ? '⚠️ Em desenvolvimento' : '🔴 Crítico';
    return { cat, pct, criticos, emDesenv, fortes, status, info: ANALISE_CATEGORIA[cat] };
  }).sort((a, b) => a.pct - b.pct);

  // ── Recomendações priorizadas ──
  const recsOrdem = ['CRÍTICA', 'ALTA', 'MÉDIA'];
  const recomendacoes = questoesRH
    .filter(q => (Number(diagRH.respostas?.[q.id]) || 0) < 5)
    .map(q => {
      const nota = Number(diagRH.respostas?.[q.id]) || 0;
      const rec = RECOMENDACOES_POR_QUESTAO[q.id];
      if (!rec) return null;
      // Eleva prioridade se nota = 0 em questão de prioridade ALTA
      const prioridade = nota === 0 && rec.prioridade === 'ALTA' ? 'CRÍTICA' : rec.prioridade;
      return { ...rec, prioridade, questao: q.texto, nota, categoria: q.categoria };
    })
    .filter(Boolean)
    .sort((a, b) => recsOrdem.indexOf(a.prioridade) - recsOrdem.indexOf(b.prioridade));

  // ── Análise cruzada com pesquisa de funcionários ──
  let alertasCruzados = [];
  let resumoPesquisa = '';
  if (pesquisa?.respostas?.length > 0) {
    const qtdResp = pesquisa.respostas.length;
    const pct = (qid, valor) => {
      const n = pesquisa.respostas.filter(r => r.respostas?.[qid] === valor).length;
      return Math.round(n / qtdResp * 100);
    };

    // Indicadores de risco combinados
    const estresseAlto = pct('f1', 'Alto');
    const saudeMentalAfetada = pct('f2', 'Sim');
    const semEquilibrio = pct('f3', 'Não');
    const semSuporteEmpresa = pct('f4', 'Não');
    const semHabitosSaudaveis = pct('f5', 'Não');
    const mudancaNegativa = pct('f8', 'Sim');
    const semAcolhimento = pct('f9', 'Não');

    if (estresseAlto >= 40) alertasCruzados.push(`🔴 ALERTA CRÍTICO: ${estresseAlto}% dos funcionários relatam estresse ALTO — risco elevado de afastamentos e turnover`);
    if (saudeMentalAfetada >= 30) alertasCruzados.push(`🔴 ALERTA: ${saudeMentalAfetada}% relatam que o trabalho afeta negativamente a saúde mental`);
    if (semSuporteEmpresa >= 40) alertasCruzados.push(`⚠️ DIVERGÊNCIA: ${semSuporteEmpresa}% não percebem suporte da empresa — contradiz ações declaradas pelo RH`);
    if (semEquilibrio >= 50) alertasCruzados.push(`⚠️ ALERTA: ${semEquilibrio}% relatam desequilíbrio entre vida pessoal e profissional`);
    if (mudancaNegativa >= 30) alertasCruzados.push(`🔴 ALERTA: ${mudancaNegativa}% notaram piora na saúde mental desde que ingressaram na empresa`);
    if (semAcolhimento >= 40) alertasCruzados.push(`⚠️ DIVERGÊNCIA: ${semAcolhimento}% não se sentem acolhidos pelo RH — verificar efetividade dos canais de suporte`);

    // Verificar divergência RH vs Funcionários
    const rhDeclarouCanais = (Number(diagRH.respostas?.['q6']) || 0) >= 3;
    if (rhDeclarouCanais && semAcolhimento >= 40) {
      alertasCruzados.push(`⚠️ DIVERGÊNCIA ESTRUTURAL: RH declarou canais de comunicação existentes (q6), mas ${semAcolhimento}% dos funcionários não se sentem acolhidos — canais podem existir mas não são efetivos`);
    }
    const rhDeclarouProgramas = (Number(diagRH.respostas?.['q17']) || 0) >= 3;
    if (rhDeclarouProgramas && semSuporteEmpresa >= 40) {
      alertasCruzados.push(`⚠️ DIVERGÊNCIA ESTRUTURAL: RH declarou programas de apoio (q17), mas ${semSuporteEmpresa}% dos funcionários não percebem suporte — comunicação dos programas pode ser insuficiente`);
    }

    // Resumo quantitativo da pesquisa
    const questoesFuncionariosAtivas = pesquisa.questoes || QUESTOES_FUNCIONARIOS;
    resumoPesquisa = `\n### 📊 Resultados da Pesquisa com Funcionários (${qtdResp} respondente${qtdResp > 1 ? 's' : ''})\n\n`;
    questoesFuncionariosAtivas.forEach(q => {
      const contagem = {};
      pesquisa.respostas.forEach(r => { const v = r.respostas?.[q.id]; if (v) contagem[v] = (contagem[v] || 0) + 1; });
      const total_c = Object.values(contagem).reduce((s, v) => s + v, 0);
      if (total_c === 0) return;
      resumoPesquisa += `**${q.texto}**\n`;
      Object.entries(contagem).sort((a, b) => b[1] - a[1]).forEach(([k, v]) => {
        const barra = '█'.repeat(Math.round(v / total_c * 10)) + '░'.repeat(10 - Math.round(v / total_c * 10));
        resumoPesquisa += `  ${barra} ${k}: ${v} (${Math.round(v / total_c * 100)}%)\n`;
      });
      resumoPesquisa += '\n';
    });
  }

  // ── Conformidade NR-1 ──
  const itensNR1 = [
    { item: 'Identificação e avaliação de riscos psicossociais', questoes: ['q1','q2'], ok: false },
    { item: 'Integração ao PGR', questoes: ['q3'], ok: false },
    { item: 'Responsável designado', questoes: ['q4'], ok: false },
    { item: 'Monitoramento e indicadores', questoes: ['q5','q8'], ok: false },
    { item: 'Canal de comunicação seguro', questoes: ['q6'], ok: false },
    { item: 'Políticas antissédio formalizadas', questoes: ['q9'], ok: false },
    { item: 'Plano de ação contínuo', questoes: ['q16'], ok: false },
    { item: 'Documentação de evidências', questoes: ['q15','q20'], ok: false },
  ].map(item => {
    const mediaItem = item.questoes.reduce((s, qid) => s + (Number(diagRH.respostas?.[qid]) || 0), 0) / item.questoes.length;
    return { ...item, ok: mediaItem >= 3, mediaItem: Math.round(mediaItem) };
  });

  const conformidade = Math.round(itensNR1.filter(i => i.ok).length / itensNR1.length * 100);

  // ── Helpers de linguagem natural ──
  const empresa = diagRH.empresa || 'a organização';
  const dataFmt = diagRH.data ? new Date(diagRH.data + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' }) : diagRH.data;

  const descNivel = {
    1: `A avaliação revela que ${empresa} ainda está em fase inicial de adequação à NR-1 no que se refere à gestão de riscos psicossociais. As iniciativas identificadas são predominantemente reativas e carecem de formalização, continuidade e integração entre as áreas. Esse cenário representa exposição jurídica relevante, especialmente diante da vigência plena da norma e do crescente rigor das fiscalizações.`,
    2: `${empresa} demonstra esforços concretos na direção da conformidade com a NR-1, com iniciativas identificadas em diferentes dimensões da gestão de riscos psicossociais. No entanto, a inconsistência na aplicação, a ausência de registros sistemáticos e a falta de integração entre RH, SST e lideranças ainda comprometem a capacidade de comprovar conformidade perante uma fiscalização. Há uma janela importante de melhoria que, se bem aproveitada nos próximos meses, pode consolidar a empresa em um patamar maduro.`,
    3: `${empresa} apresenta maturidade expressiva na gestão de riscos psicossociais, com práticas estruturadas, documentadas e conectadas à estratégia organizacional. A pontuação alcançada reflete um trabalho consistente e comprometimento da liderança com o tema. O desafio agora é manter esse padrão, aprofundar a mensuração de resultados e garantir que as evidências de conformidade estejam sempre atualizadas e acessíveis.`,
  };

  const catCritica = analiseCategoria.filter(a => a.pct < 50);
  const catDesenv = analiseCategoria.filter(a => a.pct >= 50 && a.pct < 80);
  const catOk = analiseCategoria.filter(a => a.pct >= 80);

  const sinteseCategorias = () => {
    const partes = [];
    if (catCritica.length > 0) partes.push(`As dimensões de ${catCritica.map(a => a.cat).join(' e ')} demandam atenção prioritária, com lacunas que precisam ser endereçadas antes de qualquer auditoria ou fiscalização.`);
    if (catDesenv.length > 0) partes.push(`${catDesenv.map(a => a.cat).join(', ')} ${catDesenv.length === 1 ? 'está' : 'estão'} em trajetória de desenvolvimento — as práticas existem, mas ainda de forma irregular ou sem registro adequado.`);
    if (catOk.length > 0) partes.push(`${catOk.map(a => a.cat).join(' e ')} ${catOk.length === 1 ? 'figura' : 'figuram'} como ponto${catOk.length > 1 ? 's' : ''} forte${catOk.length > 1 ? 's' : ''} da gestão atual, com práticas consolidadas que servem de referência interna.`);
    return partes.join(' ');
  };

  // ── Montagem narrativa ──
  let texto = `RELATÓRIO DE DIAGNÓSTICO NR-1\n`;
  texto += `Avaliação de Riscos Psicossociais — Conformidade com a NR-1\n`;
  texto += `${'─'.repeat(60)}\n\n`;

  if (diagRH.empresa) texto += `Empresa: ${empresa}\n`;
  texto += `Data da avaliação: ${dataFmt}\n`;
  texto += `Pontuação obtida: ${totalNormalizado}/100 pontos\n`;
  texto += `Nível de maturidade: ${info.label}\n`;
  texto += `Conformidade com itens obrigatórios NR-1: ${conformidade}%\n`;
  texto += `\n${'─'.repeat(60)}\n\n`;

  texto += `PARECER DO CONSULTOR\n\n`;
  texto += `${descNivel[nivel]}\n\n`;
  if (sinteseCategorias()) texto += `${sinteseCategorias()}\n\n`;

  // Conformidade NR-1
  texto += `${'─'.repeat(60)}\n`;
  texto += `CONFORMIDADE COM REQUISITOS OBRIGATÓRIOS DA NR-1\n\n`;
  const naoConformes = itensNR1.filter(i => !i.ok);
  const conformes = itensNR1.filter(i => i.ok);
  if (conformes.length > 0) {
    texto += `Requisitos atendidos (${conformes.length}):\n`;
    conformes.forEach(i => { texto += `  ✓  ${i.item}\n`; });
    texto += '\n';
  }
  if (naoConformes.length > 0) {
    texto += `Requisitos com lacunas ou ausentes (${naoConformes.length}):\n`;
    naoConformes.forEach(i => { texto += `  ✗  ${i.item}\n`; });
    texto += '\n';
  }
  if (naoConformes.length === 0) {
    texto += `Todos os requisitos obrigatórios foram identificados como atendidos. Recomenda-se manter registros atualizados como evidência perante fiscalizações.\n\n`;
  } else {
    texto += `Os ${naoConformes.length} item(ns) acima representam exposição direta a autuações. Sua regularização deve ser tratada com prioridade máxima.\n\n`;
  }

  // Análise por dimensão
  texto += `${'─'.repeat(60)}\n`;
  texto += `ANÁLISE POR DIMENSÃO\n\n`;

  const verbosCategoria = {
    Estrutura: (pct) => pct >= 80 ? `A estrutura de governança para riscos psicossociais está bem estabelecida.` : pct >= 50 ? `A estrutura de governança existe parcialmente, mas ainda carece de formalização e integração.` : `A base estrutural para gestão de riscos psicossociais ainda não foi estabelecida.`,
    Monitoramento: (pct) => pct >= 80 ? `Os mecanismos de monitoramento estão ativos e fornecem dados relevantes para a tomada de decisão.` : pct >= 50 ? `Há instrumentos de monitoramento em uso, mas sem regularidade ou análise sistematizada dos dados.` : `A empresa não possui mecanismos efetivos para identificar e acompanhar riscos psicossociais no cotidiano.`,
    Políticas: (pct) => pct >= 80 ? `As políticas de proteção ao trabalhador estão formalizadas e amplamente comunicadas.` : pct >= 50 ? `Existem políticas, mas sua comunicação e aplicação ainda são inconsistentes.` : `Faltam políticas formais para proteção contra assédio, sobrecarga e saúde mental.`,
    Integração: (pct) => pct >= 80 ? `Há forte integração entre RH, SST e lideranças, com dados sendo usados estrategicamente.` : pct >= 50 ? `A integração entre áreas ocorre pontualmente, sem fluxo sistemático de informações.` : `As iniciativas de saúde mental operam de forma isolada, sem integração entre as áreas responsáveis.`,
    Programas: (pct) => pct >= 80 ? `Os programas de suporte ao colaborador são concretos, mensuráveis e percebidos positivamente.` : pct >= 50 ? `Existem iniciativas de bem-estar, mas sem mensuração de impacto ou continuidade garantida.` : `A empresa ainda não oferece programas estruturados de apoio à saúde mental dos colaboradores.`,
  };

  analiseCategoria.forEach(({ cat, pct, criticos, emDesenv, fortes }) => {
    const icone = pct >= 80 ? '▲' : pct >= 50 ? '◆' : '▼';
    texto += `${icone} ${cat}  —  ${pct}% de maturidade\n`;
    const verbo = verbosCategoria[cat];
    if (verbo) texto += `${verbo(pct)}\n`;
    if (criticos.length > 0) {
      texto += `Ausente ou inexistente:\n`;
      criticos.forEach(q => { texto += `   — ${q.texto}\n`; });
    }
    if (emDesenv.length > 0) {
      texto += `Realizado parcialmente, sem padrão:\n`;
      emDesenv.forEach(q => { texto += `   ~ ${q.texto}\n`; });
    }
    if (fortes.length > 0) {
      texto += `Bem estabelecido e estruturado:\n`;
      fortes.forEach(q => { texto += `   + ${q.texto}\n`; });
    }
    texto += '\n';
  });

  // Alertas cruzados — narrativo
  if (alertasCruzados.length > 0) {
    texto += `${'─'.repeat(60)}\n`;
    texto += `SINAIS DE ALERTA — ANÁLISE CRUZADA RH × FUNCIONÁRIOS\n\n`;
    texto += `O cruzamento entre a avaliação do RH e a percepção dos funcionários revelou inconsistências relevantes que merecem atenção imediata:\n\n`;
    alertasCruzados.forEach((a, i) => {
      const limpo = a.replace(/^[🔴⚠️]\s*(ALERTA CRÍTICO:|ALERTA:|DIVERGÊNCIA ESTRUTURAL:|DIVERGÊNCIA:)?\s*/u, '');
      texto += `${i + 1}. ${limpo}\n`;
    });
    texto += '\n';
  }

  // Pesquisa de funcionários — narrativa
  if (resumoPesquisa) texto += resumoPesquisa;

  // Plano de ação — narrativo
  texto += `${'─'.repeat(60)}\n`;
  texto += `PLANO DE AÇÃO PRIORIZADO\n\n`;

  const criticas = recomendacoes.filter(r => r.prioridade === 'CRÍTICA');
  const altas = recomendacoes.filter(r => r.prioridade === 'ALTA');
  const medias = recomendacoes.filter(r => r.prioridade === 'MÉDIA');

  if (criticas.length > 0) {
    texto += `Ações imediatas — devem ser iniciadas em até 30 dias\n`;
    texto += `Estas ações endereçam lacunas que expõem a empresa a riscos legais diretos ou comprometem a base da conformidade NR-1.\n\n`;
    criticas.forEach((r, i) => {
      texto += `${i + 1}. ${r.acao}\n`;
      texto += `   Área: ${r.categoria}   |   Prazo: ${r.prazo}   |   Condutor: ${r.responsavel}\n\n`;
    });
  }
  if (altas.length > 0) {
    texto += `Ações estruturantes — entre 30 e 60 dias\n`;
    texto += `Consolidam os processos de gestão de riscos psicossociais e fortalecem a capacidade de monitoramento e resposta.\n\n`;
    altas.forEach((r, i) => {
      texto += `${i + 1}. ${r.acao}\n`;
      texto += `   Área: ${r.categoria}   |   Prazo: ${r.prazo}   |   Condutor: ${r.responsavel}\n\n`;
    });
  }
  if (medias.length > 0) {
    texto += `Ações complementares — entre 60 e 90 dias\n`;
    texto += `Ampliam a percepção positiva dos colaboradores e reforçam a cultura de saúde mental na organização.\n\n`;
    medias.forEach((r, i) => {
      texto += `${i + 1}. ${r.acao}\n`;
      texto += `   Área: ${r.categoria}   |   Prazo: ${r.prazo}   |   Condutor: ${r.responsavel}\n\n`;
    });
  }

  // Considerações finais
  texto += `${'─'.repeat(60)}\n`;
  texto += `CONSIDERAÇÕES FINAIS\n\n`;
  if (nivel === 1) {
    texto += `A jornada de adequação à NR-1 em ${empresa} precisa começar pela base: governança, mapeamento e responsabilização. Sem esses pilares, qualquer ação pontual tende a ser ineficaz. Recomenda-se que a liderança assuma o compromisso público com o tema e que seja constituído um grupo de trabalho multidisciplinar para conduzir o processo de implantação.\n\n`;
    texto += `Com dedicação e execução disciplinada das ações prioritárias, é factível que a empresa alcance o nível 2 de maturidade em um ciclo de 90 dias.\n`;
  } else if (nivel === 2) {
    texto += `${empresa} está em um momento estratégico: as bases existem, mas a consolidação exige consistência. O principal desafio não é criar do zero, mas sistematizar o que já funciona e documentar adequadamente. Gestores de linha têm papel central nessa fase — precisam ser capacitados e engajados como agentes de saúde mental em suas equipes.\n\n`;
    texto += `Uma revisão semestral dos indicadores e a manutenção do plano de ação ativo são suficientes para sustentar a evolução ao nível 3 nos próximos 6 a 12 meses.\n`;
  } else {
    texto += `${empresa} está entre as organizações mais preparadas para a NR-1. O risco nesse estágio não é de descumprimento, mas de complacência — é natural que iniciativas maduras percam intensidade sem um gatilho externo. Recomenda-se a realização de um ciclo anual de reavaliação, com atualização do mapeamento de riscos e renovação do plano de ação, mantendo o engajamento da liderança com o tema.\n`;
  }

  return texto;
}

// ─── Estilos ──────────────────────────────────────────────────────────────────
const sc = {
  container: { padding: '24px', color: '#1c1530', fontFamily: 'Inter, sans-serif' },
  h1: { fontSize: 22, fontWeight: 700, color: '#1c1530', margin: 0 },
  sub: { fontSize: 13, color: '#5f5874', marginTop: 4 },
  tabs: { display: 'flex', gap: 8, marginTop: 20, marginBottom: 24, borderBottom: '1px solid #e5e0ef', paddingBottom: 0 },
  tab: (ativo) => ({ padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer', color: ativo ? '#1d4ed8' : '#5f5874', fontWeight: ativo ? 600 : 400, fontSize: 14, borderBottom: ativo ? '2px solid #58a6ff' : '2px solid transparent', marginBottom: -1 }),
  card: { background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 10, padding: 20, marginBottom: 16 },
  cardTitulo: { fontSize: 15, fontWeight: 600, color: '#1c1530', marginBottom: 12 },
  input: { width: '100%', background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 6, padding: '8px 12px', color: '#1c1530', fontSize: 13, boxSizing: 'border-box' },
  label: { fontSize: 12, color: '#5f5874', marginBottom: 4, display: 'block' },
  btn: (cor = '#238636') => ({ background: cor, color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 500 }),
  btnOutline: { background: 'transparent', color: '#1d4ed8', border: '1px solid #388bfd', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 12 },
  btnX: { background: 'none', border: 'none', cursor: 'pointer', color: '#5f5874', padding: 0 },
  badge: (cor) => ({ display: 'inline-block', padding: '2px 10px', borderRadius: 20, background: cor + '22', color: cor, fontSize: 12, fontWeight: 600 }),
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  opcBtn: (sel) => ({ padding: '8px 16px', border: `1px solid ${sel ? '#58a6ff' : '#e5e0ef'}`, borderRadius: 6, background: sel ? 'rgba(88,166,255,0.12)' : 'transparent', color: sel ? '#1d4ed8' : '#5f5874', cursor: 'pointer', fontSize: 13, fontWeight: sel ? 600 : 400 }),
  pontuacaoBtn: (sel, val) => {
    const cores = { 0: '#ef4444', 3: '#f59e0b', 5: '#22c55e' };
    return { padding: '6px 20px', border: `1px solid ${sel ? cores[val] : '#e5e0ef'}`, borderRadius: 6, background: sel ? cores[val] + '22' : 'transparent', color: sel ? cores[val] : '#5f5874', cursor: 'pointer', fontSize: 13, fontWeight: sel ? 700 : 400 };
  },
  usrBtn: (sel) => ({ padding: '4px 10px', border: `1px solid ${sel ? '#7c3aed' : '#e5e0ef'}`, borderRadius: 20, background: sel ? 'rgba(124,58,237,0.15)' : 'transparent', color: sel ? '#6d28d9' : '#64748b', cursor: 'pointer', fontSize: 12 }),
  btnIcone: (cor) => ({ background: 'transparent', border: 'none', color: cor, cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }),
  btnFechar: { background: 'transparent', border: 'none', color: '#5f5874', cursor: 'pointer', display: 'flex', alignItems: 'center' },
  btnAcao: (cor) => ({ background: cor, color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', cursor: 'pointer', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center' }),
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' },
  modal: { background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 14, padding: '1.5rem', width: '100%', maxWidth: 500, display: 'flex', flexDirection: 'column' },
};

// ─── Componente principal ─────────────────────────────────────────────────────
export function DiagnosticoNR1({ dadosNR1 = {}, setDadosNR1, usuarioAtual, isAdmin, usuarios = [], perfisAcesso = [] }) {
  const userId = String(usuarioAtual?.id || '');

  // Permissões
  const pa = useMemo(() => perfisAcesso.find(p => String(p.id) === String(usuarioAtual?.perfil_acesso_id)), [perfisAcesso, usuarioAtual]);
  const tipoNR1 = isAdmin ? 'gestor' : (pa?.permissoes?.diagnosticoNR1?.tipo || '');
  const podeVerEtapa1 = isAdmin || tipoNR1 === 'rh';
  const podeVerEtapa2 = isAdmin || tipoNR1 === 'funcionario';
  const podeVerEtapa3 = isAdmin || tipoNR1 === 'gestor';
  const podeGerenciar = isAdmin;
  const podeResponderEtapa1 = tipoNR1 === 'rh';
  const podeResponderEtapa2 = tipoNR1 === 'funcionario';

  const [aba, setAba] = useState('etapa1');

  // Corrige aba ativa quando permissões carregam via Supabase (async)
  useEffect(() => {
    setAba(prev => {
      const tabAcessivel = (prev === 'etapa1' && podeVerEtapa1) ||
                           (prev === 'etapa2' && podeVerEtapa2) ||
                           (prev === 'etapa3' && podeVerEtapa3) ||
                           (prev === 'historico' && podeGerenciar);
      if (tabAcessivel) return prev;
      if (podeVerEtapa1) return 'etapa1';
      if (podeVerEtapa2) return 'etapa2';
      if (podeVerEtapa3) return 'etapa3';
      return prev;
    });
  }, [podeVerEtapa1, podeVerEtapa2, podeVerEtapa3]);

  // Dados
  const diagnosticosRH = dadosNR1.diagnosticosRH || [];
  const pesquisasFuncionarios = dadosNR1.pesquisasFuncionarios || [];
  const relatorios = dadosNR1.relatorios || [];

  // Questões customizáveis — fallback para as padrão
  const questoesRH = (dadosNR1.questoesRH && dadosNR1.questoesRH.length > 0) ? dadosNR1.questoesRH : QUESTOES_RH;
  const questoesFuncionarios = (dadosNR1.questoesFuncionarios && dadosNR1.questoesFuncionarios.length > 0) ? dadosNR1.questoesFuncionarios : QUESTOES_FUNCIONARIOS;

  const setDiagnosticosRH = (fn) => setDadosNR1(prev => { const d = prev || {}; return { ...d, diagnosticosRH: typeof fn === 'function' ? fn(d.diagnosticosRH || []) : fn }; });
  const setPesquisas = (fn) => setDadosNR1(prev => { const d = prev || {}; return { ...d, pesquisasFuncionarios: typeof fn === 'function' ? fn(d.pesquisasFuncionarios || []) : fn }; });
  const setRelatorios = (fn) => setDadosNR1(prev => { const d = prev || {}; return { ...d, relatorios: typeof fn === 'function' ? fn(d.relatorios || []) : fn }; });
  const setQuestoesRH = (v) => setDadosNR1(prev => { const d = prev || {}; return { ...d, questoesRH: v }; });
  const setQuestoesFuncionarios = (v) => setDadosNR1(prev => { const d = prev || {}; return { ...d, questoesFuncionarios: v }; });

  // Filtragem por permissão
  const diagVisiveis = podeGerenciar ? diagnosticosRH : diagnosticosRH.filter(d => d.criadoPor === userId);
  const pesqVisiveis = podeGerenciar
    ? pesquisasFuncionarios
    : pesquisasFuncionarios.filter(p => p.status === 'aberta' || (p.respostas || []).some(r => r.userId === userId));

  return (
    <div style={sc.container}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={sc.h1}>Diagnóstico NR-1</h1>
          <p style={sc.sub}>Avaliação de Riscos Psicossociais conforme NR-1</p>
        </div>
      </div>

      {!podeVerEtapa1 && !podeVerEtapa2 && !podeVerEtapa3 && (
        <div style={{ ...sc.card, textAlign: 'center', padding: 40, color: '#5f5874' }}>
          <FileText size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
          <p style={{ fontSize: 15 }}>Você não tem acesso ao módulo Diagnóstico NR-1.</p>
          <p style={{ fontSize: 13 }}>Solicite ao administrador a configuração do seu perfil de acesso.</p>
        </div>
      )}

      <div style={sc.tabs}>
        {podeVerEtapa1 && <button style={sc.tab(aba === 'etapa1')} onClick={() => setAba('etapa1')}>Etapa 1 — Diagnóstico RH</button>}
        {podeVerEtapa2 && <button style={sc.tab(aba === 'etapa2')} onClick={() => setAba('etapa2')}>Etapa 2 — Pesquisa Funcionários</button>}
        {podeVerEtapa3 && <button style={sc.tab(aba === 'etapa3')} onClick={() => setAba('etapa3')}>Etapa 3 — Análise e Relatório</button>}
        {isAdmin && <button style={sc.tab(aba === 'config')} onClick={() => setAba('config')}>⚙ Perguntas</button>}
        {podeGerenciar && <button style={sc.tab(aba === 'historico')} onClick={() => setAba('historico')}>📊 Histórico</button>}
      </div>

      {aba === 'etapa1' && podeVerEtapa1 && (
        <Etapa1
          diagnosticos={diagVisiveis}
          setDiagnosticos={setDiagnosticosRH}
          podeGerenciar={podeGerenciar}
          podeResponder={podeResponderEtapa1}
          usuarios={usuarios}
          userId={userId}
          usuarioAtual={usuarioAtual}
          questoesRH={questoesRH}
        />
      )}
      {aba === 'etapa2' && podeVerEtapa2 && (
        <Etapa2
          pesquisas={pesqVisiveis}
          setPesquisas={setPesquisas}
          podeGerenciar={podeGerenciar}
          podeResponder={podeResponderEtapa2}
          usuarios={usuarios}
          userId={userId}
          usuarioAtual={usuarioAtual}
          questoesFuncionarios={questoesFuncionarios}
        />
      )}
      {aba === 'etapa3' && podeVerEtapa3 && (
        <Etapa3
          diagnosticos={diagnosticosRH}
          pesquisas={pesquisasFuncionarios}
          relatorios={relatorios}
          setRelatorios={setRelatorios}
          podeGerenciar={podeGerenciar}
          usuarios={usuarios}
          userId={userId}
          usuarioAtual={usuarioAtual}
          questoesRH={questoesRH}
        />
      )}
      {aba === 'config' && isAdmin && (
        <EtapaConfig
          questoesRH={questoesRH}
          setQuestoesRH={setQuestoesRH}
          questoesFuncionarios={questoesFuncionarios}
          setQuestoesFuncionarios={setQuestoesFuncionarios}
          questoesRHPadrao={QUESTOES_RH}
          questoesFuncionariosPadrao={QUESTOES_FUNCIONARIOS}
          categoriasDisponiveis={CATEGORIAS}
        />
      )}
      {aba === 'historico' && podeGerenciar && (
        <HistoricoCiclos
          diagnosticos={diagnosticosRH}
          pesquisas={pesquisasFuncionarios}
          relatorios={relatorios}
          usuarios={usuarios}
        />
      )}
    </div>
  );
}

// ─── Etapa 1 — Diagnóstico RH ─────────────────────────────────────────────────
const FORM_INICIAL_E1 = { titulo: '', empresa: '', data: new Date().toISOString().slice(0, 10), usuariosPermitidos: [], respostas: {} };

function Etapa1({ diagnosticos, setDiagnosticos, podeGerenciar, podeResponder, usuarios, userId, usuarioAtual, questoesRH = QUESTOES_RH }) {
  const [diagSel, setDiagSel] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [form, setForm] = useState(FORM_INICIAL_E1);

  const jaRespondeuEtapa1 = podeResponder && diagnosticos.some(d => d.criadoPor === userId);

  function abrirNovo() {
    setForm({ ...FORM_INICIAL_E1, data: new Date().toISOString().slice(0, 10) });
    setEditandoId(null);
    setShowForm(true);
    setDiagSel(null);
  }

  function abrirResponder() {
    const data = new Date().toISOString().slice(0, 10);
    setForm({ ...FORM_INICIAL_E1, titulo: `Questionário RH — ${data}`, data, usuariosPermitidos: [userId] });
    setEditandoId(null);
    setShowForm(true);
    setDiagSel(null);
  }

  function abrirEditar(diag) {
    setForm({ titulo: diag.titulo, empresa: diag.empresa || '', data: diag.data, usuariosPermitidos: diag.usuariosPermitidos || [], respostas: { ...diag.respostas } });
    setEditandoId(diag.id);
    setShowForm(true);
    setDiagSel(null);
  }

  function salvar() {
    if (!form.titulo.trim()) { toast.erro('Informe o título do diagnóstico.'); return; }
    const total = calcularTotal(form.respostas);
    const nivel = calcularNivel(total);
    if (editandoId) {
      setDiagnosticos(prev => prev.map(d => d.id === editandoId ? { ...d, ...form, total, nivel } : d));
      toast.sucesso('Diagnóstico atualizado!');
    } else {
      const novo = { id: novoId(), ...form, total, nivel, criadoPor: userId, status: 'finalizado', criadoEm: new Date().toISOString() };
      setDiagnosticos(prev => [...prev, novo]);
      toast.sucesso('Diagnóstico salvo!');
    }
    setShowForm(false);
    setEditandoId(null);
  }

  function excluir(id) {
    if (!window.confirm('Excluir este diagnóstico?')) return;
    setDiagnosticos(prev => prev.filter(d => d.id !== id));
    if (diagSel?.id === id) setDiagSel(null);
  }

  function toggleUsuario(uid) {
    setForm(f => {
      const ids = f.usuariosPermitidos || [];
      return { ...f, usuariosPermitidos: ids.includes(uid) ? ids.filter(x => x !== uid) : [...ids, uid] };
    });
  }

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
        {podeGerenciar && (
          <button style={sc.btn()} onClick={abrirNovo}><Plus size={14} style={{ marginRight: 4 }} />Novo Diagnóstico RH</button>
        )}
        {podeResponder && !showForm && !jaRespondeuEtapa1 && (
          <button style={sc.btn('#1d6fa8')} onClick={abrirResponder}><ClipboardList size={14} style={{ marginRight: 4 }} />Responder Questionário</button>
        )}
        {podeResponder && jaRespondeuEtapa1 && (
          <span style={sc.badge('#22c55e')}>✓ Questionário já respondido</span>
        )}
      </div>

      {showForm && (
        <div style={sc.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <span style={sc.cardTitulo}>{editandoId ? 'Editar Diagnóstico RH' : 'Novo Diagnóstico RH'}</span>
            <button style={sc.btnX} onClick={() => { setShowForm(false); setEditandoId(null); }}><X size={16} /></button>
          </div>

          <div className="form-grid-3" style={{ gap: 12, marginBottom: 12 }}>
            <div>
              <label style={sc.label}>Título *</label>
              <input style={sc.input} value={form.titulo} onChange={e => setForm(f => ({ ...f, titulo: e.target.value }))} placeholder="Ex: Diagnóstico RH — Mar/2026" />
            </div>
            <div>
              <label style={sc.label}>Empresa</label>
              <input style={sc.input} value={form.empresa} onChange={e => setForm(f => ({ ...f, empresa: e.target.value }))} placeholder="Nome da empresa" />
            </div>
            <div>
              <label style={sc.label}>Data</label>
              <input style={sc.input} type="date" value={form.data} onChange={e => setForm(f => ({ ...f, data: e.target.value }))} />
            </div>
          </div>

          {podeGerenciar && usuarios.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <label style={sc.label}>Usuários autorizados a preencher</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {usuarios.map(u => (
                  <button key={u.id} style={sc.usrBtn((form.usuariosPermitidos || []).includes(String(u.id)))} onClick={() => toggleUsuario(String(u.id))}>
                    {(form.usuariosPermitidos || []).includes(String(u.id)) ? '✓ ' : '+ '}{u.nome || u.email}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: '#5f5874', marginBottom: 12 }}>
              Pontuação: <strong style={{ color: '#1c1530' }}>0</strong> = não faz / inexistente &nbsp;|&nbsp;
              <strong style={{ color: '#f59e0b' }}>3</strong> = faz às vezes, sem padrão &nbsp;|&nbsp;
              <strong style={{ color: '#22c55e' }}>5</strong> = faz bem, de forma estruturada
            </div>
            {[...new Set(questoesRH.map(q => q.categoria))].map(cat => (
              <div key={cat} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#1d4ed8', marginBottom: 8 }}>{cat}</div>
                {questoesRH.filter(q => q.categoria === cat).map(q => (
                  <div key={q.id} style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, padding: 12, marginBottom: 8 }}>
                    <p style={{ fontSize: 13, color: '#2d2640', marginBottom: 8 }}>{q.texto}</p>
                    <div style={{ display: 'flex', gap: 8 }}>
                      {[0, 3, 5].map(val => (
                        <button key={val} style={sc.pontuacaoBtn(form.respostas?.[q.id] === val, val)} onClick={() => setForm(f => ({ ...f, respostas: { ...f.respostas, [q.id]: val } }))}>
                          {val}
                        </button>
                      ))}
                      {form.respostas?.[q.id] !== undefined && (
                        <span style={{ fontSize: 11, color: '#5f5874', alignSelf: 'center', marginLeft: 8 }}>
                          {form.respostas[q.id] === 0 ? 'Não realiza' : form.respostas[q.id] === 3 ? 'Em desenvolvimento' : 'Consolidado'}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ ...sc.badge(NIVEIS[calcularNivel(calcularTotal(form.respostas)) - 1]?.cor || '#5f5874'), fontSize: 14, padding: '6px 16px' }}>
              Total: {calcularTotal(form.respostas)}/100 — Nível {calcularNivel(calcularTotal(form.respostas))}
            </div>
            <button style={sc.btn()} onClick={salvar}>{editandoId ? 'Salvar Alterações' : 'Salvar Diagnóstico'}</button>
            <button style={sc.btn('#6e7681')} onClick={() => { setShowForm(false); setEditandoId(null); }}>Cancelar</button>
          </div>
        </div>
      )}

      {diagnosticos.length === 0 && !showForm && (
        <div style={{ textAlign: 'center', color: '#5f5874', padding: '48px 0' }}>
          <ClipboardList size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
          <p>Nenhum diagnóstico disponível.</p>
        </div>
      )}

      {diagnosticos.map(diag => {
        const info = NIVEIS.find(n => n.nivel === diag.nivel) || NIVEIS[0];
        const aberto = diagSel?.id === diag.id;
        return (
          <div key={diag.id} style={sc.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setDiagSel(aberto ? null : diag)}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: '#1c1530' }}>{diag.titulo}</span>
                  {diag.empresa && <span style={{ fontSize: 12, color: '#6d28d9', background: 'rgba(124,58,237,0.12)', padding: '2px 8px', borderRadius: 4 }}>{diag.empresa}</span>}
                  <span style={sc.badge(info.cor)}>Nível {diag.nivel} — {info.label}</span>
                  <span style={{ fontSize: 12, color: '#5f5874' }}>{diag.total}/100 pts</span>
                </div>
                <span style={{ fontSize: 12, color: '#5f5874' }}>{diag.data}</span>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {podeGerenciar && <button style={{ ...sc.btnOutline, fontSize: 12 }} onClick={e => { e.stopPropagation(); abrirEditar(diag); }}><Edit2 size={13} /></button>}
                {podeGerenciar && <button style={{ ...sc.btn('#b91c1c'), fontSize: 12 }} onClick={e => { e.stopPropagation(); excluir(diag.id); }}><Trash2 size={13} /></button>}
                {aberto ? <ChevronDown size={18} color="#5f5874" /> : <ChevronRight size={18} color="#5f5874" />}
              </div>
            </div>

            {aberto && (
              <div style={{ marginTop: 16 }}>
                <GraficoRH diag={diag} questoesRH={questoesRH} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function GraficoRH({ diag, questoesRH = QUESTOES_RH }) {
  const categorias = [...new Set(questoesRH.map(q => q.categoria))];
  const dadosRadar = categorias.map(cat => {
    const qs = questoesRH.filter(q => q.categoria === cat);
    const maxPossivel = qs.length * 5;
    const obtido = qs.reduce((s, q) => s + (Number(diag.respostas?.[q.id]) || 0), 0);
    return { categoria: cat, valor: maxPossivel > 0 ? Math.round(obtido / maxPossivel * 100) : 0 };
  });

  const dadosBarra = questoesRH.map(q => ({
    nome: `Q${q.id.replace('q', '')}`,
    valor: Number(diag.respostas?.[q.id]) || 0,
    fill: (Number(diag.respostas?.[q.id]) || 0) === 5 ? '#22c55e' : (Number(diag.respostas?.[q.id]) || 0) === 3 ? '#f59e0b' : '#ef4444',
  }));

  return (
    <div className="mobile-grid-1" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
      <div>
        <p style={{ fontSize: 13, color: '#5f5874', marginBottom: 8 }}>Maturidade por Categoria (%)</p>
        <ResponsiveContainer width="100%" height={220}>
          <RadarChart data={dadosRadar}>
            <PolarGrid stroke="#e5e0ef" />
            <PolarAngleAxis dataKey="categoria" tick={{ fill: '#5f5874', fontSize: 11 }} />
            <Radar name="Score" dataKey="valor" stroke="#58a6ff" fill="#58a6ff" fillOpacity={0.25} />
          </RadarChart>
        </ResponsiveContainer>
      </div>
      <div>
        <p style={{ fontSize: 13, color: '#5f5874', marginBottom: 8 }}>Pontuação por Questão</p>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={dadosBarra} margin={{ top: 4, right: 4, left: -20, bottom: 4 }}>
            <XAxis dataKey="nome" tick={{ fill: '#5f5874', fontSize: 10 }} />
            <YAxis domain={[0, 5]} tick={{ fill: '#5f5874', fontSize: 10 }} ticks={[0, 3, 5]} />
            <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #e5e0ef', color: '#1c1530' }} />
            <Bar dataKey="valor" radius={[3, 3, 0, 0]}>
              {dadosBarra.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ─── Etapa 2 — Pesquisa com Funcionários ─────────────────────────────────────
const FORM_INICIAL_E2 = { titulo: '', empresa: '', data: new Date().toISOString().slice(0, 10), usuariosPermitidos: [] };

function Etapa2({ pesquisas, setPesquisas, podeGerenciar, podeResponder, usuarios, userId, usuarioAtual, questoesFuncionarios = QUESTOES_FUNCIONARIOS }) {
  const [pesqSel, setPesqSel] = useState(null);
  const [showFormNova, setShowFormNova] = useState(false);
  const [editandoPesqId, setEditandoPesqId] = useState(null);
  const [formNova, setFormNova] = useState(FORM_INICIAL_E2);
  const [respostasForm, setRespostasForm] = useState({});
  const [nomeRespondente, setNomeRespondente] = useState(usuarioAtual?.nome || '');
  const [observacoes, setObservacoes] = useState('');
  const [respondido, setRespondido] = useState(false);

  function abrirNovaPesquisa() {
    setFormNova({ ...FORM_INICIAL_E2, data: new Date().toISOString().slice(0, 10) });
    setEditandoPesqId(null);
    setShowFormNova(true);
  }

  function abrirEditarPesquisa(pesq) {
    setFormNova({ titulo: pesq.titulo, empresa: pesq.empresa || '', data: pesq.data, usuariosPermitidos: pesq.usuariosPermitidos || [] });
    setEditandoPesqId(pesq.id);
    setShowFormNova(true);
    setPesqSel(null);
  }

  function criarPesquisa() {
    if (!formNova.titulo.trim()) { toast.erro('Informe o título da pesquisa.'); return; }
    if (editandoPesqId) {
      setPesquisas(prev => prev.map(p => p.id === editandoPesqId ? { ...p, titulo: formNova.titulo, empresa: formNova.empresa, data: formNova.data, usuariosPermitidos: formNova.usuariosPermitidos } : p));
      toast.sucesso('Pesquisa atualizada!');
    } else {
      const nova = { id: novoId(), ...formNova, questoes: questoesFuncionarios, respostas: [], criadoPor: userId, status: 'aberta', criadoEm: new Date().toISOString() };
      setPesquisas(prev => [...prev, nova]);
      toast.sucesso('Pesquisa criada!');
    }
    setShowFormNova(false);
    setEditandoPesqId(null);
  }

  function toggleUsuarioPesquisa(uid) {
    setFormNova(f => {
      const ids = f.usuariosPermitidos || [];
      return { ...f, usuariosPermitidos: ids.includes(uid) ? ids.filter(x => x !== uid) : [...ids, uid] };
    });
  }

  function enviarResposta(pesquisa) {
    const faltando = pesquisa.questoes.filter(q => !respostasForm[q.id]);
    if (faltando.length > 0) { toast.erro(`Responda todas as questões (${faltando.length} pendente(s)).`); return; }

    const jaRespondeu = pesquisa.respostas.some(r => r.userId === userId);
    if (jaRespondeu) { toast.erro('Você já respondeu esta pesquisa.'); return; }

    const novaResp = { userId, data: new Date().toISOString().slice(0, 10), respostas: respostasForm, observacoes };
    setPesquisas(prev => prev.map(p => p.id === pesquisa.id ? { ...p, respostas: [...p.respostas, novaResp] } : p));
    toast.sucesso('Resposta enviada!');
    setRespostasForm({});
    setObservacoes('');
    setRespondido(true);
    setPesqSel(null);
  }

  function encerrar(id) {
    if (!window.confirm('Encerrar esta pesquisa? Não aceitará mais respostas.')) return;
    setPesquisas(prev => prev.map(p => p.id === id ? { ...p, status: 'encerrada' } : p));
  }

  function excluir(id) {
    if (!window.confirm('Excluir esta pesquisa?')) return;
    setPesquisas(prev => prev.filter(p => p.id !== id));
    if (pesqSel?.id === id) setPesqSel(null);
  }

  return (
    <div>
      {podeGerenciar && (
        <button style={sc.btn()} onClick={abrirNovaPesquisa}><Plus size={14} style={{ marginRight: 4 }} />Nova Pesquisa com Funcionários</button>
      )}

      {showFormNova && (
        <div style={{ ...sc.card, marginTop: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <span style={sc.cardTitulo}>{editandoPesqId ? 'Editar Pesquisa' : 'Nova Pesquisa de Funcionários'}</span>
            <button style={sc.btnX} onClick={() => { setShowFormNova(false); setEditandoPesqId(null); }}><X size={16} /></button>
          </div>
          <div className="form-grid-3" style={{ gap: 12, marginBottom: 12 }}>
            <div>
              <label style={sc.label}>Título *</label>
              <input style={sc.input} value={formNova.titulo} onChange={e => setFormNova(f => ({ ...f, titulo: e.target.value }))} placeholder="Ex: Pesquisa Funcionários — Mar/2026" />
            </div>
            <div>
              <label style={sc.label}>Empresa</label>
              <input style={sc.input} value={formNova.empresa} onChange={e => setFormNova(f => ({ ...f, empresa: e.target.value }))} placeholder="Nome da empresa" />
            </div>
            <div>
              <label style={sc.label}>Data</label>
              <input style={sc.input} type="date" value={formNova.data} onChange={e => setFormNova(f => ({ ...f, data: e.target.value }))} />
            </div>
          </div>
          {usuarios.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <label style={sc.label}>Funcionários que podem responder</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {usuarios.map(u => (
                  <button key={u.id} style={sc.usrBtn((formNova.usuariosPermitidos || []).includes(String(u.id)))} onClick={() => toggleUsuarioPesquisa(String(u.id))}>
                    {(formNova.usuariosPermitidos || []).includes(String(u.id)) ? '✓ ' : '+ '}{u.nome || u.email}
                  </button>
                ))}
              </div>
            </div>
          )}
          {!editandoPesqId && <div style={{ fontSize: 12, color: '#5f5874', marginBottom: 12 }}>As questões configuradas serão aplicadas automaticamente ({questoesFuncionarios.length} questões).</div>}
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={sc.btn()} onClick={criarPesquisa}>{editandoPesqId ? 'Salvar Alterações' : 'Criar Pesquisa'}</button>
            <button style={sc.btn('#6e7681')} onClick={() => { setShowFormNova(false); setEditandoPesqId(null); }}>Cancelar</button>
          </div>
        </div>
      )}

      {pesquisas.length === 0 && !showFormNova && (
        <div style={{ textAlign: 'center', color: '#5f5874', padding: '48px 0', marginTop: 16 }}>
          <Users size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
          <p>Nenhuma pesquisa disponível.</p>
        </div>
      )}

      <div style={{ marginTop: 16 }}>
        {pesquisas.map(pesq => {
          const jaRespondeu = pesq.respostas?.some(r => r.userId === userId);
          const aberto = pesqSel?.id === pesq.id;
          return (
            <div key={pesq.id} style={sc.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#1c1530' }}>{pesq.titulo}</span>
                    {pesq.empresa && <span style={{ fontSize: 12, color: '#6d28d9', background: 'rgba(124,58,237,0.12)', padding: '2px 8px', borderRadius: 4 }}>{pesq.empresa}</span>}
                    <span style={sc.badge(pesq.status === 'aberta' ? '#22c55e' : '#6e7681')}>{pesq.status === 'aberta' ? 'Aberta' : 'Encerrada'}</span>
                    <span style={{ fontSize: 12, color: '#5f5874' }}>{pesq.respostas?.length || 0} resposta(s)</span>
                  </div>
                  <span style={{ fontSize: 12, color: '#5f5874' }}>{pesq.data}</span>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {podeGerenciar && <button style={{ ...sc.btnOutline, fontSize: 12 }} onClick={() => abrirEditarPesquisa(pesq)}><Edit2 size={13} /></button>}
                  {podeGerenciar && pesq.status === 'aberta' && <button style={{ ...sc.btnOutline, fontSize: 12 }} onClick={() => encerrar(pesq.id)}>Encerrar</button>}
                  {podeGerenciar && <button style={{ ...sc.btn('#b91c1c'), fontSize: 12 }} onClick={() => excluir(pesq.id)}><Trash2 size={13} /></button>}
                  {!podeGerenciar && pesq.status === 'aberta' && !jaRespondeu && (
                    <button style={sc.btn()} onClick={() => { setPesqSel(aberto ? null : pesq); setRespostasForm({}); setRespondido(false); }}>Responder</button>
                  )}
                  {jaRespondeu && <span style={sc.badge('#22c55e')}>✓ Respondida</span>}
                  {podeGerenciar && (
                    <button style={sc.btnOutline} onClick={() => setPesqSel(aberto ? null : pesq)}>
                      {aberto ? 'Fechar' : 'Ver respostas'}
                    </button>
                  )}
                </div>
              </div>

              {/* Formulário de resposta (funcionários) */}
              {aberto && !podeGerenciar && pesq.status === 'aberta' && !jaRespondeu && (
                <div style={{ marginTop: 16 }}>
                  {pesq.questoes.map((q, i) => (
                    <div key={q.id} style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, padding: 14, marginBottom: 10 }}>
                      <p style={{ fontSize: 13, color: '#2d2640', marginBottom: 10 }}><strong style={{ color: '#5f5874' }}>{i + 1}.</strong> {q.texto}</p>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        {q.opcoes.map(op => (
                          <button key={op} style={sc.opcBtn(respostasForm[q.id] === op)} onClick={() => setRespostasForm(f => ({ ...f, [q.id]: op }))}>
                            {op}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div style={{ marginBottom: 12 }}>
                    <label style={sc.label}>Observações e sugestões (opcional)</label>
                    <textarea style={{ ...sc.input, minHeight: 60, resize: 'vertical' }} value={observacoes} onChange={e => setObservacoes(e.target.value)} placeholder="Escreva aqui..." />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button style={sc.btn()} onClick={() => enviarResposta(pesq)}>Enviar Resposta</button>
                    <button style={sc.btn('#6e7681')} onClick={() => setPesqSel(null)}>Cancelar</button>
                  </div>
                </div>
              )}

              {/* Respostas (admin) */}
              {aberto && podeGerenciar && (
                <div style={{ marginTop: 16 }}>
                  <GraficoPesquisa pesquisa={pesq} />
                  <div style={{ marginTop: 16 }}>
                    <p style={{ fontSize: 13, color: '#5f5874', marginBottom: 8 }}>Respostas individuais</p>
                    {(pesq.respostas || []).length === 0 && <p style={{ color: '#5f5874', fontSize: 13 }}>Nenhuma resposta ainda.</p>}
                    {(pesq.respostas || []).map((r, i) => (
                      <div key={i} style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, padding: 12, marginBottom: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                          <strong style={{ fontSize: 13, color: '#1c1530' }}>{usuarios.find(u => String(u.id) === String(r.userId))?.nome || r.userId}</strong>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ fontSize: 12, color: '#5f5874' }}>{r.data}</span>
                            <button style={sc.btnIcone('#ef4444')} title="Excluir resposta (libera o usuário para responder novamente)" onClick={() => {
                              if (!window.confirm(`Excluir resposta de "${r.nome}"? O usuário poderá responder novamente.`)) return;
                              setPesquisas(prev => prev.map(p => p.id === pesq.id ? { ...p, respostas: p.respostas.filter((_, j) => j !== i) } : p));
                            }}><Trash2 size={13} /></button>
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                          {pesq.questoes.map(q => (
                            <span key={q.id} style={{ fontSize: 12, color: '#2d2640', background: '#ffffff', padding: '2px 8px', borderRadius: 4 }}>
                              Q{q.id.replace('f', '')}: <strong>{r.respostas?.[q.id] || '—'}</strong>
                            </span>
                          ))}
                        </div>
                        {r.observacoes && <p style={{ fontSize: 12, color: '#5f5874', marginTop: 8 }}>"{r.observacoes}"</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GraficoPesquisa({ pesquisa }) {
  const total = pesquisa.respostas?.length || 0;
  if (total === 0) return <p style={{ color: '#5f5874', fontSize: 13 }}>Sem respostas para exibir gráficos.</p>;

  const dadosPizza = QUESTOES_FUNCIONARIOS.slice(0, 4).map(q => {
    const contagem = {};
    pesquisa.respostas.forEach(r => { const v = r.respostas?.[q.id]; if (v) contagem[v] = (contagem[v] || 0) + 1; });
    return { questao: q.texto.slice(0, 40) + '...', dados: Object.entries(contagem).map(([name, value]) => ({ name, value })) };
  });

  return (
    <div>
      <p style={{ fontSize: 13, color: '#5f5874', marginBottom: 12 }}>Distribuição das respostas ({total} respondentes)</p>
      <div className="mobile-grid-1" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        {dadosPizza.map((item, i) => (
          <div key={i}>
            <p style={{ fontSize: 11, color: '#5f5874', marginBottom: 4 }}>{item.questao}</p>
            <ResponsiveContainer width="100%" height={140}>
              <PieChart>
                <Pie data={item.dados} cx="50%" cy="50%" outerRadius={50} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                  {item.dados.map((_, idx) => <Cell key={idx} fill={CORES_PIZZA[idx % CORES_PIZZA.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: '#ffffff', border: '1px solid #e5e0ef', color: '#1c1530', fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Etapa 3 — Análise e Relatório ────────────────────────────────────────────
function Etapa3({ diagnosticos, pesquisas, relatorios, setRelatorios, podeGerenciar, usuarios, userId, usuarioAtual, questoesRH = QUESTOES_RH }) {
  const [diagId, setDiagId] = useState('');
  const [pesqIds, setPesqIds] = useState([]);
  const [relSel, setRelSel] = useState(null);
  const [planosForm, setPlanosForm] = useState([]);
  const [obsConsultor, setObsConsultor] = useState('');
  const [mostrarAnalise, setMostrarAnalise] = useState(false);

  // Filtros da lista de relatórios
  const [filtroEmpresa, setFiltroEmpresa] = useState('');
  const [filtroDtDe, setFiltroDtDe] = useState('');
  const [filtroDtAte, setFiltroDtAte] = useState('');
  const [filtroAluno, setFiltroAluno] = useState('');

  const diagSel = diagnosticos.find(d => d.id === diagId);

  // Mescla respostas de múltiplas pesquisas para análise
  const pesqMesclada = useMemo(() => {
    const selecionadas = pesquisas.filter(p => pesqIds.includes(p.id));
    if (selecionadas.length === 0) return null;
    const todasRespostas = selecionadas.flatMap(p => p.respostas || []);
    const questoes = selecionadas[0]?.questoes || QUESTOES_FUNCIONARIOS;
    return { ...selecionadas[0], respostas: todasRespostas, questoes, titulo: selecionadas.length === 1 ? selecionadas[0].titulo : `${selecionadas.length} pesquisas combinadas` };
  }, [pesqIds, pesquisas]);

  const textoRelatorio = useMemo(() => gerarRelatorioTexto(diagSel, pesqMesclada, questoesRH), [diagSel, pesqMesclada, questoesRH]);

  const togglePesq = (id) => setPesqIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // Empresas únicas para o filtro
  const empresasUnicas = useMemo(() => {
    const set = new Set();
    diagnosticos.forEach(d => { if (d.empresa) set.add(d.empresa); });
    pesquisas.forEach(p => { if (p.empresa) set.add(p.empresa); });
    return [...set].sort();
  }, [diagnosticos, pesquisas]);

  // Relatórios filtrados
  const relatoriosFiltrados = useMemo(() => relatorios.filter(rel => {
    const diag = diagnosticos.find(d => d.id === rel.diagnosticoRHId);
    const pesq = pesquisas.find(p => p.id === rel.pesquisaFuncionariosId);
    const empresa = diag?.empresa || pesq?.empresa || '';
    if (filtroEmpresa && !empresa.toLowerCase().includes(filtroEmpresa.toLowerCase())) return false;
    if (filtroDtDe && rel.data < filtroDtDe) return false;
    if (filtroDtAte && rel.data > filtroDtAte) return false;
    if (filtroAluno) {
      const respondentes = (pesq?.respostas || []).map(r => (r.nome || '').toLowerCase());
      if (!respondentes.some(n => n.includes(filtroAluno.toLowerCase()))) return false;
    }
    return true;
  }), [relatorios, diagnosticos, pesquisas, filtroEmpresa, filtroDtDe, filtroDtAte, filtroAluno]);

  function gerarRelatorio() {
    if (!diagId) { toast.erro('Selecione um Diagnóstico RH.'); return; }
    const novo = {
      id: novoId(),
      diagnosticoRHId: diagId,
      pesquisaFuncionariosId: pesqIds.length === 1 ? pesqIds[0] : null,
      pesquisaFuncionariosIds: pesqIds.length > 0 ? pesqIds : null,
      data: new Date().toISOString().slice(0, 10),
      geradoPor: userId,
      geradoEm: new Date().toISOString(),
      textoRelatorio,
      validadoPor: null,
      validadoEm: null,
      planosAcao: [],
      observacoesConsultor: '',
    };
    setRelatorios(prev => [...prev, novo]);
    toast.sucesso('Relatório gerado!');
    setRelSel(novo);
    setMostrarAnalise(false);
  }

  function validar(relId) {
    setRelatorios(prev => prev.map(r => r.id === relId ? { ...r, validadoPor: usuarioAtual?.nome || userId, validadoEm: new Date().toISOString(), planosAcao: planosForm, observacoesConsultor: obsConsultor } : r));
    toast.sucesso('Relatório validado!');
  }

  function excluirRel(id) {
    if (!window.confirm('Excluir este relatório?')) return;
    setRelatorios(prev => prev.filter(r => r.id !== id));
    if (relSel?.id === id) setRelSel(null);
  }

  function addPlano() {
    setPlanosForm(prev => [...prev, { id: novoId(), acao: '', responsavel: '', prazo: '', status: 'pendente' }]);
  }

  function imprimirRelatorio(rel) {
    const diag = diagnosticos.find(d => d.id === rel.diagnosticoRHId);
    const pesq = pesquisas.find(p => p.id === rel.pesquisaFuncionariosId);
    const empresa = diag?.empresa || pesq?.empresa || '';
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>Relatório NR-1${empresa ? ' — ' + empresa : ''}</title>
      <style>body{font-family:Arial,sans-serif;padding:32px;color:#111;max-width:800px;margin:0 auto}
      pre{white-space:pre-wrap;font-family:Arial,sans-serif;font-size:14px;line-height:1.7}
      h2{color:#1e3a5f}table{width:100%;border-collapse:collapse;margin-top:12px}
      td,th{border:1px solid #ccc;padding:8px;font-size:13px}th{background:#f0f0f0}
      .badge{display:inline-block;padding:2px 10px;border-radius:12px;font-size:12px;font-weight:600}
      @media print{button{display:none}}</style></head><body>
      <h2>Relatório de Diagnóstico NR-1${empresa ? ' — ' + empresa : ''}</h2>
      <p><strong>Data:</strong> ${rel.data} &nbsp;|&nbsp; <strong>Gerado por:</strong> ${rel.geradoPor}</p>
      ${rel.validadoPor ? `<p><strong>Validado por:</strong> ${rel.validadoPor} em ${rel.validadoEm?.slice(0,10)}</p>` : ''}
      <pre>${rel.textoRelatorio}</pre>
      ${rel.observacoesConsultor ? `<h3>Observações do Consultor</h3><p>${rel.observacoesConsultor}</p>` : ''}
      ${rel.planosAcao?.length ? `<h3>Planos de Ação</h3><table><tr><th>Ação</th><th>Responsável</th><th>Prazo</th><th>Status</th></tr>
        ${rel.planosAcao.map(pl => `<tr><td>${pl.acao}</td><td>${pl.responsavel||'—'}</td><td>${pl.prazo||'—'}</td><td>${pl.status}</td></tr>`).join('')}
      </table>` : ''}
      <script>window.print();</script></body></html>`);
    win.document.close();
  }

  return (
    <div>
      {/* Gerador de relatório */}
      {podeGerenciar && (
        <div style={sc.card}>
          <span style={sc.cardTitulo}>Gerar Novo Relatório</span>
          <div style={{ marginBottom: 14, marginTop: 12 }}>
            <label style={sc.label}>Diagnóstico RH *</label>
            <select style={sc.input} value={diagId} onChange={e => setDiagId(e.target.value)}>
              <option value="">Selecione...</option>
              {diagnosticos.map(d => <option key={d.id} value={d.id}>{d.empresa ? `[${d.empresa}] ` : ''}{d.titulo} ({d.data})</option>)}
            </select>
          </div>

          {pesquisas.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <label style={sc.label}>Pesquisas de Funcionários para análise (opcional — selecione uma ou mais)</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 6, padding: 10, maxHeight: 180, overflowY: 'auto' }}>
                {pesquisas.map(p => {
                  const sel = pesqIds.includes(p.id);
                  return (
                    <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: sel ? '#1d4ed8' : '#2d2640' }}>
                      <input type="checkbox" checked={sel} onChange={() => togglePesq(p.id)} style={{ accentColor: '#1d4ed8' }} />
                      {p.empresa ? `[${p.empresa}] ` : ''}{p.titulo}
                      <span style={{ fontSize: 11, color: '#5f5874' }}>({p.respostas?.length || 0} resp. · {p.status})</span>
                    </label>
                  );
                })}
              </div>
              {pesqIds.length > 0 && (
                <p style={{ fontSize: 11, color: '#1d4ed8', margin: '4px 0 0' }}>
                  {pesqIds.length} pesquisa(s) selecionada(s) — {pesquisas.filter(p => pesqIds.includes(p.id)).reduce((s, p) => s + (p.respostas?.length || 0), 0)} respostas serão analisadas
                </p>
              )}
            </div>
          )}

          {diagSel && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <label style={sc.label}>Pré-visualização da análise</label>
                <button style={{ ...sc.btnOutline, fontSize: 11 }} onClick={() => setMostrarAnalise(v => !v)}>
                  {mostrarAnalise ? 'Recolher' : 'Expandir análise'}
                </button>
              </div>
              <div style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, padding: 16, maxHeight: mostrarAnalise ? 'none' : 240, overflowY: mostrarAnalise ? 'visible' : 'auto' }}>
                <pre style={{ fontSize: 12, color: '#2d2640', whiteSpace: 'pre-wrap', margin: 0, lineHeight: 1.7 }}>{textoRelatorio}</pre>
              </div>
            </div>
          )}

          <button style={sc.btn()} onClick={gerarRelatorio}><FileText size={14} style={{ marginRight: 4 }} />Gerar e Salvar Relatório</button>
        </div>
      )}

      {/* Filtros */}
      <div style={{ ...sc.card, padding: 16 }}>
        <span style={{ ...sc.cardTitulo, fontSize: 13 }}>Filtrar relatórios</span>
        <div className="mobile-grid-1" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10, marginTop: 10 }}>
          <div>
            <label style={sc.label}>Empresa</label>
            {empresasUnicas.length > 0 ? (
              <select style={sc.input} value={filtroEmpresa} onChange={e => setFiltroEmpresa(e.target.value)}>
                <option value="">Todas</option>
                {empresasUnicas.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            ) : (
              <input style={sc.input} value={filtroEmpresa} onChange={e => setFiltroEmpresa(e.target.value)} placeholder="Nome da empresa" />
            )}
          </div>
          <div>
            <label style={sc.label}>Data de</label>
            <input style={sc.input} type="date" value={filtroDtDe} onChange={e => setFiltroDtDe(e.target.value)} />
          </div>
          <div>
            <label style={sc.label}>Data até</label>
            <input style={sc.input} type="date" value={filtroDtAte} onChange={e => setFiltroDtAte(e.target.value)} />
          </div>
          <div>
            <label style={sc.label}>Respondente (aluno)</label>
            <input style={sc.input} value={filtroAluno} onChange={e => setFiltroAluno(e.target.value)} placeholder="Nome do respondente" />
          </div>
        </div>
        {(filtroEmpresa || filtroDtDe || filtroDtAte || filtroAluno) && (
          <button style={{ ...sc.btnOutline, marginTop: 8, fontSize: 12 }} onClick={() => { setFiltroEmpresa(''); setFiltroDtDe(''); setFiltroDtAte(''); setFiltroAluno(''); }}>
            Limpar filtros
          </button>
        )}
      </div>

      {/* Lista de relatórios */}
      {relatoriosFiltrados.length === 0 && (
        <div style={{ textAlign: 'center', color: '#5f5874', padding: '48px 0' }}>
          <BarChart2 size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
          <p>{relatorios.length === 0 ? 'Nenhum relatório gerado ainda.' : 'Nenhum relatório encontrado com os filtros aplicados.'}</p>
        </div>
      )}

      {relatoriosFiltrados.map(rel => {
        const diag = diagnosticos.find(d => d.id === rel.diagnosticoRHId);
        const pesq = pesquisas.find(p => p.id === rel.pesquisaFuncionariosId);
        const empresa = diag?.empresa || pesq?.empresa || '';
        const aberto = relSel?.id === rel.id;
        return (
          <div key={rel.id} style={sc.card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => setRelSel(aberto ? null : rel)}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <span style={{ fontSize: 15, fontWeight: 600, color: '#1c1530' }}>{diag?.titulo || 'Relatório'}</span>
                  {empresa && <span style={{ fontSize: 12, color: '#6d28d9', background: 'rgba(124,58,237,0.12)', padding: '2px 8px', borderRadius: 4 }}>{empresa}</span>}
                  <span style={sc.badge(rel.validadoPor ? '#22c55e' : '#f59e0b')}>{rel.validadoPor ? '✓ Validado' : 'Aguardando validação'}</span>
                </div>
                <span style={{ fontSize: 12, color: '#5f5874' }}>{rel.data}{pesq ? ` · ${pesq.titulo}` : ''}{rel.validadoPor ? ` · Validado por ${rel.validadoPor}` : ''}</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {podeGerenciar && <button style={{ ...sc.btnOutline, fontSize: 12 }} onClick={e => { e.stopPropagation(); const d = diagnosticos.find(x => x.id === rel.diagnosticoRHId); const ids = rel.pesquisaFuncionariosIds || (rel.pesquisaFuncionariosId ? [rel.pesquisaFuncionariosId] : []); const ps = pesquisas.filter(x => ids.includes(x.id)); const mesclada = ps.length > 0 ? { ...ps[0], respostas: ps.flatMap(p => p.respostas || []), questoes: ps[0].questoes || QUESTOES_FUNCIONARIOS } : null; const novoTexto = gerarRelatorioTexto(d, mesclada, questoesRH); setRelatorios(prev => prev.map(r => r.id === rel.id ? { ...r, textoRelatorio: novoTexto } : r)); toast.sucesso('Relatório reanalisado com sucesso.'); }}>↺ Reanalisar</button>}
                {podeGerenciar && <button style={{ ...sc.btnOutline, fontSize: 12 }} onClick={e => { e.stopPropagation(); imprimirRelatorio(rel); }}>Imprimir</button>}
                {podeGerenciar && <button style={{ ...sc.btn('#b91c1c'), fontSize: 12 }} onClick={e => { e.stopPropagation(); excluirRel(rel.id); }}><Trash2 size={13} /></button>}
                {aberto ? <ChevronDown size={18} color="#5f5874" /> : <ChevronRight size={18} color="#5f5874" />}
              </div>
            </div>

            {aberto && (
              <div style={{ marginTop: 16 }}>
                {diag && <GraficoRH diag={diag} />}

                <div style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, padding: 16, marginTop: 16 }}>
                  <p style={{ fontSize: 13, color: '#1d4ed8', fontWeight: 600, marginBottom: 8 }}>Relatório Gerado</p>
                  <pre style={{ fontSize: 12, color: '#2d2640', whiteSpace: 'pre-wrap', fontFamily: 'monospace', lineHeight: 1.6 }}>{rel.textoRelatorio}</pre>
                </div>

                {/* Observações do consultor */}
                {podeGerenciar && !rel.validadoPor && (
                  <div style={{ marginTop: 16 }}>
                    <p style={{ fontSize: 13, color: '#1c1530', fontWeight: 600, marginBottom: 8 }}>Validação e Planos de Ação</p>
                    <div style={{ marginBottom: 12 }}>
                      <label style={sc.label}>Observações do consultor</label>
                      <textarea style={{ ...sc.input, minHeight: 80, resize: 'vertical' }} value={obsConsultor} onChange={e => setObsConsultor(e.target.value)} placeholder="Adicione suas observações e conclusões..." />
                    </div>

                    <div style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <label style={sc.label}>Planos de Ação</label>
                        <button style={sc.btnOutline} onClick={addPlano}><Plus size={12} style={{ marginRight: 4 }} />Adicionar</button>
                      </div>
                      {planosForm.map((pl, i) => (
                        <div key={pl.id} className="mobile-grid-1" style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, padding: 12, marginBottom: 8, display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 8, alignItems: 'center' }}>
                          <input style={sc.input} placeholder="Ação *" value={pl.acao} onChange={e => setPlanosForm(prev => prev.map((p, j) => j === i ? { ...p, acao: e.target.value } : p))} />
                          <input style={sc.input} placeholder="Responsável" value={pl.responsavel} onChange={e => setPlanosForm(prev => prev.map((p, j) => j === i ? { ...p, responsavel: e.target.value } : p))} />
                          <input style={sc.input} type="date" value={pl.prazo} onChange={e => setPlanosForm(prev => prev.map((p, j) => j === i ? { ...p, prazo: e.target.value } : p))} />
                          <button style={sc.btnX} onClick={() => setPlanosForm(prev => prev.filter((_, j) => j !== i))}><X size={15} /></button>
                        </div>
                      ))}
                    </div>

                    <div style={{ display: 'flex', gap: 8 }}>
                      <button style={sc.btn('#1d6fa8')} onClick={() => validar(rel.id)}><CheckCircle size={14} style={{ marginRight: 4 }} />Validar Relatório</button>
                    </div>
                  </div>
                )}

                {/* Planos validados */}
                {rel.planosAcao?.length > 0 && (
                  <div style={{ marginTop: 16 }}>
                    <p style={{ fontSize: 13, color: '#1c1530', fontWeight: 600, marginBottom: 8 }}>Planos de Ação</p>
                    {rel.planosAcao.map((pl, i) => (
                      <div key={i} style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, padding: 10, marginBottom: 6, display: 'flex', gap: 12, alignItems: 'center', fontSize: 13 }}>
                        <span style={{ flex: 2, color: '#2d2640' }}>{pl.acao}</span>
                        {pl.responsavel && <span style={{ color: '#5f5874' }}>{pl.responsavel}</span>}
                        {pl.prazo && <span style={{ color: '#5f5874' }}>{pl.prazo}</span>}
                        <span style={sc.badge('#f59e0b')}>{pl.status}</span>
                      </div>
                    ))}
                  </div>
                )}

                {rel.observacoesConsultor && (
                  <div style={{ marginTop: 12, background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, padding: 12 }}>
                    <p style={{ fontSize: 12, color: '#1d4ed8', marginBottom: 4 }}>Observações do consultor</p>
                    <p style={{ fontSize: 13, color: '#2d2640' }}>{rel.observacoesConsultor}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Histórico de Ciclos ───────────────────────────────────────────────────────
function HistoricoCiclos({ diagnosticos, pesquisas, relatorios, usuarios }) {
  const [filtroEmpresa, setFiltroEmpresa] = useState('');

  const ciclos = useMemo(() => {
    return relatorios.map(rel => {
      const diag = diagnosticos.find(d => d.id === rel.diagnosticoRHId);
      const pesqIds = rel.pesquisaFuncionariosIds || (rel.pesquisaFuncionariosId ? [rel.pesquisaFuncionariosId] : []);
      const pesqsSel = pesquisas.filter(p => pesqIds.includes(p.id));
      const totalRespondentes = pesqsSel.reduce((s, p) => s + (p.respostas?.length || 0), 0);
      const empresaNome = diag?.empresa || pesqsSel[0]?.empresa || '';
      const scoreRH = diag?.total ?? null;
      const nivel = diag?.nivel ?? null;
      return {
        id: rel.id,
        data: rel.data,
        empresa: empresaNome,
        scoreRH,
        nivel,
        titulo: diag?.titulo || 'Diagnóstico',
        respondentes: totalRespondentes,
        validado: !!rel.validadoPor,
        validadoPor: rel.validadoPor,
        geradoEm: rel.geradoEm,
        numPesquisas: pesqsSel.length,
      };
    }).sort((a, b) => a.data.localeCompare(b.data));
  }, [relatorios, diagnosticos, pesquisas]);

  const empresas = useMemo(() => [...new Set(ciclos.map(c => c.empresa).filter(Boolean))].sort(), [ciclos]);
  const ciclosFiltrados = filtroEmpresa ? ciclos.filter(c => c.empresa === filtroEmpresa) : ciclos;

  const dadosEvolucao = useMemo(() => ciclosFiltrados
    .filter(c => c.scoreRH !== null)
    .map(c => ({ data: c.data, score: c.scoreRH, empresa: c.empresa || 'Geral' })),
    [ciclosFiltrados]);

  const scoreMedio = ciclosFiltrados.filter(c => c.scoreRH !== null).length
    ? Math.round(ciclosFiltrados.filter(c => c.scoreRH !== null).reduce((s, c) => s + c.scoreRH, 0) / ciclosFiltrados.filter(c => c.scoreRH !== null).length)
    : null;

  function exportarCSV() {
    const linhas = [
      ['Empresa', 'Diagnóstico', 'Data', 'Score RH', 'Nível', 'Nº Pesquisas', 'Respondentes', 'Status', 'Validado por'],
      ...ciclosFiltrados.map(c => [
        c.empresa || '—',
        c.titulo,
        c.data,
        c.scoreRH !== null ? c.scoreRH : '—',
        c.nivel ? `Nível ${c.nivel} — ${NIVEIS.find(n => n.nivel === c.nivel)?.label || ''}` : '—',
        c.numPesquisas,
        c.respondentes,
        c.validado ? 'Validado' : 'Pendente',
        c.validadoPor || '—',
      ]),
    ];
    const csv = linhas.map(l => l.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `historico-nr1-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  if (relatorios.length === 0) {
    return (
      <div style={{ textAlign: 'center', color: '#5f5874', padding: '60px 0' }}>
        <BarChart2 size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
        <p>Nenhum relatório gerado ainda.</p>
        <p style={{ fontSize: 13 }}>Gere um relatório na aba Etapa 3 para visualizar o histórico.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Filtro e export */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select style={{ ...sc.input, width: 220, margin: 0 }} value={filtroEmpresa} onChange={e => setFiltroEmpresa(e.target.value)}>
            <option value="">Todas as empresas</option>
            {empresas.map(e => <option key={e} value={e}>{e}</option>)}
          </select>
          {filtroEmpresa && (
            <button style={{ ...sc.btnOutline, fontSize: 12 }} onClick={() => setFiltroEmpresa('')}>Limpar</button>
          )}
        </div>
        <button style={{ ...sc.btnOutline, fontSize: 12 }} onClick={exportarCSV}>⬇ Exportar CSV</button>
      </div>

      {/* Cards de resumo */}
      <div className="mobile-grid-1" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        {[
          { label: 'Diagnósticos', valor: ciclosFiltrados.length, cor: '#58a6ff' },
          { label: 'Validados', valor: ciclosFiltrados.filter(c => c.validado).length, cor: '#22c55e' },
          { label: 'Score médio RH', valor: scoreMedio !== null ? `${scoreMedio}/100` : '—', cor: '#f59e0b' },
          { label: 'Total respondentes', valor: ciclosFiltrados.reduce((s, c) => s + c.respondentes, 0), cor: '#a855f7' },
        ].map(({ label, valor, cor }) => (
          <div key={label} style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: cor }}>{valor}</div>
            <div style={{ fontSize: 12, color: '#5f5874', marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Gráfico de evolução (só aparece com 2+ pontos) */}
      {dadosEvolucao.length > 1 && (
        <div style={{ ...sc.card, marginBottom: 16 }}>
          <span style={sc.cardTitulo}>Evolução do Score RH</span>
          <div style={{ marginTop: 12 }}>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={dadosEvolucao} margin={{ top: 8, right: 16, left: -16, bottom: 4 }}>
                <CartesianGrid stroke="#eeeaf5" strokeDasharray="3 3" />
                <XAxis dataKey="data" tick={{ fill: '#5f5874', fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fill: '#5f5874', fontSize: 11 }} ticks={[0, 40, 70, 100]} />
                <Tooltip
                  contentStyle={{ background: '#ffffff', border: '1px solid #e5e0ef', color: '#1c1530', fontSize: 12 }}
                  formatter={(val) => [`${val}/100`, 'Score RH']}
                />
                <ReferenceLine y={40} stroke="#ef4444" strokeDasharray="4 4" strokeOpacity={0.5} label={{ value: 'Nível 2', fill: '#ef4444', fontSize: 10, position: 'right' }} />
                <ReferenceLine y={70} stroke="#22c55e" strokeDasharray="4 4" strokeOpacity={0.5} label={{ value: 'Nível 3', fill: '#22c55e', fontSize: 10, position: 'right' }} />
                <Line type="monotone" dataKey="score" stroke="#58a6ff" strokeWidth={2} dot={{ fill: '#1d4ed8', r: 5 }} activeDot={{ r: 7 }} />
              </LineChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', gap: 20, justifyContent: 'center', marginTop: 6 }}>
              {NIVEIS.map(n => (
                <span key={n.nivel} style={{ fontSize: 11, color: n.cor }}>● {n.label} ({n.faixa})</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tabela comparativa */}
      <div style={sc.card}>
        <span style={sc.cardTitulo}>Todos os resultados ({ciclosFiltrados.length})</span>
        <div className="mobile-table-wrap" style={{ marginTop: 12, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #e5e0ef' }}>
                {['Empresa', 'Diagnóstico', 'Data', 'Score RH', 'Nível', 'Respondentes', 'Status'].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: '#5f5874', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ciclosFiltrados.map(c => {
                const nivelInfo = NIVEIS.find(n => n.nivel === c.nivel);
                return (
                  <tr key={c.id} style={{ borderBottom: '1px solid #eeeaf5' }}>
                    <td style={{ padding: '10px 12px', color: '#6d28d9', whiteSpace: 'nowrap' }}>{c.empresa || '—'}</td>
                    <td style={{ padding: '10px 12px', color: '#1c1530', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.titulo}</td>
                    <td style={{ padding: '10px 12px', color: '#5f5874', whiteSpace: 'nowrap' }}>{c.data}</td>
                    <td style={{ padding: '10px 12px' }}>
                      {c.scoreRH !== null ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 64, background: '#ffffff', borderRadius: 4, height: 7 }}>
                            <div style={{ width: `${c.scoreRH}%`, background: nivelInfo?.cor || '#5f5874', borderRadius: 4, height: '100%', transition: 'width 0.3s' }} />
                          </div>
                          <span style={{ color: '#1c1530', fontWeight: 600, minWidth: 48 }}>{c.scoreRH}/100</span>
                        </div>
                      ) : <span style={{ color: '#5f5874' }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {nivelInfo ? (
                        <span style={sc.badge(nivelInfo.cor)}>N{c.nivel} — {nivelInfo.label}</span>
                      ) : <span style={{ color: '#5f5874' }}>—</span>}
                    </td>
                    <td style={{ padding: '10px 12px', color: '#5f5874', textAlign: 'center' }}>{c.respondentes || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={sc.badge(c.validado ? '#22c55e' : '#f59e0b')}>{c.validado ? '✓ Validado' : 'Pendente'}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─── EtapaConfig — Configuração de Perguntas ──────────────────────────────────

function EtapaConfig({ questoesRH, setQuestoesRH, questoesFuncionarios, setQuestoesFuncionarios, questoesRHPadrao, questoesFuncionariosPadrao, categoriasDisponiveis }) {
  const [secao, setSecao] = useState('rh');
  const [modalAberto, setModalAberto] = useState(false);
  const [editandoQ, setEditandoQ] = useState(null);
  const [formQ, setFormQ] = useState({ texto: '', categoria: '', tipo: 'sim_nao', opcoes: '' });
  const [alvo, setAlvo] = useState('rh');

  const abrirNova = (a) => {
    setAlvo(a);
    setEditandoQ(null);
    setFormQ(a === 'rh'
      ? { texto: '', categoria: categoriasDisponiveis[0] || 'Estrutura', tipo: 'rh', opcoes: '' }
      : { texto: '', categoria: '', tipo: 'sim_nao', opcoes: 'Sim,Não' }
    );
    setModalAberto(true);
  };

  const abrirEditar = (q, a) => {
    setAlvo(a);
    setEditandoQ(q.id);
    setFormQ(a === 'rh'
      ? { texto: q.texto, categoria: q.categoria, tipo: 'rh', opcoes: '' }
      : { texto: q.texto, categoria: q.categoria || '', tipo: q.tipo || 'sim_nao', opcoes: (q.opcoes || []).join(',') }
    );
    setModalAberto(true);
  };

  const salvar = () => {
    if (!formQ.texto.trim()) return;
    if (alvo === 'rh') {
      const novaQ = { id: editandoQ || `q_${Date.now()}`, texto: formQ.texto.trim(), categoria: formQ.categoria };
      setQuestoesRH(editandoQ
        ? questoesRH.map(q => q.id === editandoQ ? novaQ : q)
        : [...questoesRH, novaQ]
      );
    } else {
      const opcs = formQ.opcoes.split(',').map(o => o.trim()).filter(Boolean);
      const novaQ = { id: editandoQ || `f_${Date.now()}`, texto: formQ.texto.trim(), tipo: formQ.tipo, opcoes: opcs.length ? opcs : ['Sim', 'Não'] };
      setQuestoesFuncionarios(editandoQ
        ? questoesFuncionarios.map(q => q.id === editandoQ ? novaQ : q)
        : [...questoesFuncionarios, novaQ]
      );
    }
    setModalAberto(false);
  };

  const excluir = (id, a) => {
    if (!window.confirm('Excluir esta pergunta?')) return;
    if (a === 'rh') setQuestoesRH(questoesRH.filter(q => q.id !== id));
    else setQuestoesFuncionarios(questoesFuncionarios.filter(q => q.id !== id));
  };

  const restaurar = (a) => {
    if (!window.confirm('Restaurar perguntas padrão? As customizações serão perdidas.')) return;
    if (a === 'rh') setQuestoesRH(questoesRHPadrao);
    else setQuestoesFuncionarios(questoesFuncionariosPadrao);
  };

  const listaAtual = secao === 'rh' ? questoesRH : questoesFuncionarios;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 4 }}>
        <button style={sc.tab(secao === 'rh')} onClick={() => setSecao('rh')}>Etapa 1 — RH</button>
        <button style={sc.tab(secao === 'func')} onClick={() => setSecao('func')}>Etapa 2 — Funcionários</button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ color: '#5f5874', fontSize: 13 }}>{listaAtual.length} pergunta(s)</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={{ ...sc.btnAcao('#6b7280'), fontSize: 12 }} onClick={() => restaurar(secao)}>Restaurar padrão</button>
          <button style={{ ...sc.btnAcao('#7c3aed'), fontSize: 12 }} onClick={() => abrirNova(secao)}><Plus size={14} style={{ marginRight: 4 }} />Nova pergunta</button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {listaAtual.map((q, i) => (
          <div key={q.id} style={{ background: '#ffffff', border: '1px solid #e5e0ef', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <span style={{ color: '#5f5874', fontSize: 12, minWidth: 24, paddingTop: 2 }}>{i + 1}.</span>
            <div style={{ flex: 1 }}>
              <p style={{ color: '#1c1530', fontSize: 13, margin: 0 }}>{q.texto}</p>
              {q.categoria && <span style={{ fontSize: 11, color: '#1d4ed8', marginTop: 4, display: 'inline-block' }}>{q.categoria}</span>}
              {q.opcoes && <span style={{ fontSize: 11, color: '#5f5874', marginLeft: 8 }}>{q.opcoes.join(' / ')}</span>}
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              <button style={sc.btnIcone('#10b981')} onClick={() => abrirEditar(q, secao)}><Edit2 size={14} /></button>
              <button style={sc.btnIcone('#ef4444')} onClick={() => excluir(q.id, secao)}><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
        {listaAtual.length === 0 && (
          <div style={{ textAlign: 'center', color: '#5f5874', padding: 32, fontSize: 13 }}>Nenhuma pergunta. Clique em "Nova pergunta" ou "Restaurar padrão".</div>
        )}
      </div>

      {modalAberto && (
        <div style={sc.overlay} onClick={() => setModalAberto(false)}>
          <div className="mobile-modal" style={sc.modal} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ color: '#1c1530', fontSize: 16, fontWeight: 700, margin: 0 }}>{editandoQ ? 'Editar' : 'Nova'} Pergunta — {alvo === 'rh' ? 'Etapa 1 (RH)' : 'Etapa 2 (Funcionários)'}</h3>
              <button style={sc.btnFechar} onClick={() => setModalAberto(false)}><X size={18} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={sc.label}>Texto da pergunta *</label>
                <textarea style={{ ...sc.input, minHeight: 80, resize: 'vertical' }} value={formQ.texto} onChange={e => setFormQ({ ...formQ, texto: e.target.value })} />
              </div>
              {alvo === 'rh' && (
                <div>
                  <label style={sc.label}>Categoria</label>
                  <select style={sc.input} value={formQ.categoria} onChange={e => setFormQ({ ...formQ, categoria: e.target.value })}>
                    {categoriasDisponiveis.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              )}
              {alvo === 'func' && (
                <>
                  <div>
                    <label style={sc.label}>Tipo</label>
                    <select style={sc.input} value={formQ.tipo} onChange={e => setFormQ({ ...formQ, tipo: e.target.value })}>
                      <option value="sim_nao">Sim / Não</option>
                      <option value="opcoes">Múltiplas opções</option>
                    </select>
                  </div>
                  <div>
                    <label style={sc.label}>Opções (separadas por vírgula)</label>
                    <input style={sc.input} value={formQ.opcoes} onChange={e => setFormQ({ ...formQ, opcoes: e.target.value })} placeholder="Sim,Não,Às vezes" />
                  </div>
                </>
              )}
            </div>
            <div className="mobile-modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button style={sc.btnAcao('#6b7280')} onClick={() => setModalAberto(false)}>Cancelar</button>
              <button style={sc.btnAcao('#7c3aed')} onClick={salvar}>Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
